import { useState, useEffect, useCallback } from 'react'
import { cn, formatDuration } from '@/lib/utils'
import { recordsApi, type ActivityRecord } from '@/lib/api'
import { CATEGORY_MAP, type Category } from '@/lib/constants'
import { Card, CardContent } from '@/components/ui/card'
import { Clock, Hash, TrendingUp, BarChart3 } from 'lucide-react'
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'

type Period = 'day' | 'week' | 'month' | 'year'

interface StatsData {
  categories: Array<{ category: string; count: number; total_duration: number }>
  summary: {
    totalCount: number
    totalDuration: number
    avgDuration: number
    topCategory: string | null
  }
  trend: Array<{ label: string; value: number }>
}

const periodLabels: Record<Period, string> = {
  day: '今日',
  week: '本周',
  month: '本月',
  year: '今年',
}

export function StatsPage() {
  const [period, setPeriod] = useState<Period>('day')
  const [data, setData] = useState<StatsData | null>(null)
  const [loading, setLoading] = useState(false)

  const fetchStats = useCallback(async () => {
    setLoading(true)
    try {
      const res = await recordsApi.stats(period)
      // res is ApiResponse<StatsData>, need to extract data field
      const apiRes = res as unknown as { code: number; data: StatsData; message: string }
      if (apiRes.code === 0) {
        // Convert string numbers to numbers
        const data = apiRes.data
        console.log('Raw stats data:', data)
        // Convert summary fields
        const summary = {
          totalCount: Number(data.summary.totalCount),
          totalDuration: Number(data.summary.totalDuration),
          avgDuration: Number(data.summary.avgDuration),
          topCategory: data.summary.topCategory
        }
        // Convert categories fields
        const categories = data.categories.map(cat => ({
          category: cat.category,
          count: Number(cat.count),
          total_duration: Number(cat.total_duration)
        }))
        // Convert trend fields
        const trend = data.trend.map(item => ({
          label: item.label,
          value: Number(item.value)
        }))
        console.log('Processed stats:', { categories, summary, trend })
        setData({ categories, summary, trend })
      } else {
        console.error('Stats API error:', apiRes.message)
        setData(null)
      }
    } catch (e) {
      console.error('Failed to fetch stats:', e)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [period])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  const pieData = (data?.categories || []).map(c => ({
    name: CATEGORY_MAP[c.category as Category]?.label || c.category,
    value: c.total_duration,
    color: CATEGORY_MAP[c.category as Category]?.color || '#999',
  }))

  //const totalDuration = data?.summary.totalDuration || 0
    
    const totalDuration = data?.summary?.totalDuration || 0
  const barData = (data?.trend || []).map(t => ({
    label: typeof t.label === 'number' ? `${t.label}` : String(t.label).slice(-5),
    value: Math.round(t.value / 60), // minutes
  }))

  return (
    <div className="flex flex-col min-h-[calc(100dvh-3.5rem)]">
      {/* Period tabs */}
      <div className="px-5 pt-4 pb-3 safe-top">
        <div className="flex gap-2 p-1 bg-muted/50 rounded-xl">
          {(['day', 'week', 'month', 'year'] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={cn(
                'flex-1 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                period === p
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {periodLabels[p]}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-6">
        {loading && !data ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !data ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <BarChart3 className="h-12 w-12 mb-3 opacity-30" />
            <p className="text-sm">暂无统计数据</p>
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-2">
                    <Clock className="h-3.5 w-3.5" />
                    <span className="text-xs">总时长</span>
                  </div>
                  <p className="text-xl font-bold">{formatDuration(totalDuration)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-2">
                    <Hash className="h-3.5 w-3.5" />
                    <span className="text-xs">记录次数</span>
                  </div>
                  <p className="text-xl font-bold">{data.summary.totalCount} 次</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-2">
                    <TrendingUp className="h-3.5 w-3.5" />
                    <span className="text-xs">最常类别</span>
                  </div>
                  <p className="text-lg font-bold">
                    {data.summary.topCategory
                      ? CATEGORY_MAP[data.summary.topCategory as Category]?.label
                      : '-'}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-2">
                    <BarChart3 className="h-3.5 w-3.5" />
                    <span className="text-xs">平均时长</span>
                  </div>
                  <p className="text-lg font-bold">{formatDuration(data.summary.avgDuration)}</p>
                </CardContent>
              </Card>
            </div>

            {/* Pie chart */}
            {pieData.length > 0 && (
              <Card className="mb-6">
                <CardContent className="p-5">
                  <h3 className="text-sm font-semibold mb-4">时间分布</h3>
                  <div className="relative">
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={85}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {pieData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value: number) => formatDuration(value)}
                          contentStyle={{ borderRadius: '12px', fontSize: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="text-center">
                        <p className="text-xl font-bold">{formatDuration(totalDuration)}</p>
                        <p className="text-[10px] text-muted-foreground">{periodLabels[period]}总计</p>
                      </div>
                    </div>
                  </div>
                  {/* Legend */}
                  <div className="flex flex-wrap gap-3 mt-4">
                    {pieData.map((item) => (
                      <div key={item.name} className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-xs text-muted-foreground">{item.name}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Bar chart */}
            {barData.length > 0 && (
              <Card className="mb-6">
                <CardContent className="p-5">
                  <h3 className="text-sm font-semibold mb-4">
                    时间趋势
                    <span className="text-xs font-normal text-muted-foreground ml-1">（分钟）</span>
                  </h3>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={barData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 10 }}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis
                        tick={{ fontSize: 10 }}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <Tooltip
                        formatter={(value: number) => [`${value}分钟`, '时长']}
                        contentStyle={{ borderRadius: '12px', fontSize: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                      />
                      <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Category details */}
            {data.categories.length > 0 && (
              <Card>
                <CardContent className="p-5">
                  <h3 className="text-sm font-semibold mb-4">类别明细</h3>
                  <div className="space-y-3">
                    {data.categories.map((cat) => {
                      const config = CATEGORY_MAP[cat.category as Category]
                      if (!config) return null
                      const pct = totalDuration > 0 ? Math.round((cat.total_duration / totalDuration) * 100) : 0
                      return (
                        <div key={cat.category} className="flex items-center gap-3">
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                            style={{ backgroundColor: config.bgColor }}
                          >
                            <config.icon className="h-4 w-4" style={{ color: config.color } as React.CSSProperties} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium">{config.label}</span>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span>{cat.count}次</span>
                                <span>{formatDuration(cat.total_duration)}</span>
                                <span className="font-medium text-foreground">{pct}%</span>
                              </div>
                            </div>
                            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all duration-500"
                                style={{ width: `${pct}%`, backgroundColor: config.color }}
                              />
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  )
}
